import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import StatusBadge from "../../components/StatusBadge";
import Loader from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import PDFViewer from "../../components/PDF";
import Dropdown from "../../components/Dropdown";
import SectionDivider from "../../components/SectionDivider";
import TextDiv from "../../components/TextDiv";
import { getBugReportById, updateBugReportStatus } from "../../services/bank/banksAnalyzeService";
import { UserAuth } from "../../utils/AuthContext";
import { getFileType } from "../../utils/files";
import BugReportAnalysisViewer from "../../components/BugReportAnalysisViewer";

const BugReportDetails = () => {
    const { t } = useTranslation("general");
    const { id } = useParams();
    const navigate = useNavigate();
    const { userData } = useContext(UserAuth);

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Status update state
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState(null);

    const statusOptions = [
        { id: "OPEN", nameInAr: t("open") },
        { id: "IN_PROGRESS", nameInAr: t("inProgress") },
        { id: "RESOLVED", nameInAr: t("resolved") },
        { id: "CLOSED", nameInAr: t("closed") },
    ];

    const fetchReport = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await getBugReportById(id);
            if (response.status === 200) {
                const data = response.data;
                setReport(data);
                // Set initial status for dropdown
                const currentStatus = statusOptions.find(opt => opt.id === data.status);
                if (currentStatus) setSelectedStatus(currentStatus);
            }
        } catch (err) {
            console.error(err);
            setError(t("errorFetchingReportDetails"));
        } finally {
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (newStatus) => {
        if (!newStatus || newStatus.id === report.status) return;

        setUpdatingStatus(true);
        try {
            await updateBugReportStatus(id, newStatus.id);
            // Refresh data
            await fetchReport();
        } catch (err) {
            console.error(err);
            // Optionally show error toast
        } finally {
            setUpdatingStatus(false);
        }
    };



    useEffect(() => {
        if (id) fetchReport();
    }, [id]);

    if (loading) return <PageWrapper withBackButton><Loader /></PageWrapper>;
    if (error) return <PageWrapper withBackButton><ErrorBox>{error}</ErrorBox></PageWrapper>;
    if (!report) return <PageWrapper withBackButton><ErrorBox>{t("reportNotFound")}</ErrorBox></PageWrapper>;

    return (
        <PageWrapper withBackButton title={`${t("bugReport")}: ${report.createdAt ? new Date(report.createdAt).toLocaleDateString() : ""}`}>
            <div className="o-column o-column--gap-size_200 u-margin--top_400">
                <div className="o-row o-row--gap-size_100 t-bold_bold">
                    <span>{t("user")}:</span>
                    <span>{report?.user?.username || "-"}</span>
                </div>
                <div className="o-row o-row--gap-size_100 t-bold_bold">
                    <span>{t("organization")}:</span>
                    <span>{report?.organization?.name || "-"}</span>
                </div>
            </div>

            <div className="u-margin--top_500 o-row o-row--horizontal-align_space-between o-row--vertical-align_center">
                <div className="o-row o-row--gap-size_200 o-row--vertical-align_center">
                    <span className="t-bold_bold">{t("status")}:</span>
                    <StatusBadge
                        color={
                            report.status === "OPEN" ? "red" :
                                report.status === "IN_PROGRESS" ? "blue" :
                                    report.status === "RESOLVED" ? "green" : "gray"
                        }
                        title={
                            report.status === "OPEN" ? t("open") :
                                report.status === "IN_PROGRESS" ? t("inProgress") :
                                    report.status === "RESOLVED" ? t("resolved") :
                                        report.status === "CLOSED" ? t("closed") : report.status
                        }
                    />
                </div>

                <div className="u-width--min-width_230">
                    <Dropdown
                        options={statusOptions}
                        selected={selectedStatus}
                        setSelected={(val) => {
                            setSelectedStatus(val);
                            handleStatusUpdate(val);
                        }}
                        thick
                        nameInAr={true}
                        disabled={updatingStatus}
                    />
                </div>
            </div>

            <SectionDivider title={t("reportDetails")} />

            <div className="o-column o-column--gap-size_400">
                <TextDiv title={t("description")} value={report.description} />

                <TextDiv
                    title="createdAt"
                    value={report.createdAt ? new Date(report.createdAt).toLocaleString() : "-"}
                />
            </div>

            <SectionDivider title={t("attachedFiles")} />

            {report.documents && report.documents.length > 0 ? (
                <div className="o-grid o-grid--auto-columns_xsmall o-grid--gap-size_700">
                    {report.documents.map((doc, index) => {
                        const baseUrl = `${process.env.REACT_APP_URL}`;

                        return <PDFViewer
                            key={index}
                            path={`${baseUrl}/${doc?.path}`}
                            filename={doc.image_type}
                            fileType={getFileType("file.pdf")}
                            downloadable={false} />
                    })}
                </div>
            ) : (
                <p>{t("noDocumentsAttached")}</p>
            )}

            <SectionDivider title={t("analysisDataPayload")} />
            <div className="u-padding_200 u-background-color_neutral-light u-border-radius_500">
                {report.analysisData ? (
                    <BugReportAnalysisViewer analyzedData={report.analysisData} />
                ) : (
                    <p>No analysis data available</p>
                )}
            </div>

        </PageWrapper>
    );
};

export default BugReportDetails;
